import { useCallback, useMemo } from "react";
import { DrawerBody, DrawerFooter } from "@chakra-ui/react";
import { pick } from "lodash";
import { assertPresence } from "utils/assertions";
import { useBrandMutation } from "utils/apollo/hooks/brand";
import useCreateValidSubmitHandler, {
  PerformUpdateHandler,
} from "components/Shared/Forms/useCreateValidSubmitHandler";
import {
  DocumentType,
  FragmentType,
  getFragmentData,
  gql,
} from "__generated__/gql";
import { CancelButton, SaveButton } from "components/Products/EditPanelContent";
import ProductLinePanelForm, {
  FORM_ID,
  processApolloError,
  productLineFields,
  ProductLineInputs,
} from "./Form";

const fragment = gql(/* GraphQL */ `
  fragment ProductLineEdit on ProductLine {
    id
    name
    shortName
    nameSeparator
    isInternalSkuIdAppended
    isArchived
    wholesaleUnitPrice
    manufacturingFacilityId
    manufacturingFacility {
      id
      ...ManufacturingFacilitySelectOption
    }
    shipsFromZip
    innerCaseQuantity
    wholesaleCaseQuantity
    productLineCategoryDepartmentSubDepartmentsList(orderBy: CREATED_AT_ASC) {
      id
      productCategoryDepartmentSubDepartment {
        id
        ...ProductCategoriesSelectOption
      }
    }
  }
`);

const mutation = gql(/* GraphQL */ `
  mutation UpdateProductLine(
    $productLineId: UUID!
    $productLineData: ProductLinePatch!
    $productCategoryIds: [UUID!]!
    $hasUnitPriceUpdate: Boolean = false
  ) {
    updateProductLine(input: { id: $productLineId, patch: $productLineData }) {
      productLine {
        id
        name
      }
    }
    setProductCategoryDepartmentSubDepartmentsForProductLine(
      input: {
        productLineId: $productLineId
        productCategoryDepartmentSubDepartmentIds: $productCategoryIds
      }
    ) {
      productLine {
        ...ProductLineFields
        ...ProductLinesSkusTableFieldsFragment
        skusList @include(if: $hasUnitPriceUpdate) {
          ...ProductsTableSku
        }
      }
    }
  }
`);

type UpdatedProductLine = NonNullable<
  DocumentType<typeof mutation>["updateProductLine"]
>["productLine"];

type Props = {
  isLoading?: boolean;
  onCancel?: () => void;
  onComplete?: () => void;
  productLine: FragmentType<typeof fragment> | null | undefined;
};

export default function ProductLineEdit({
  isLoading,
  onCancel,
  onComplete,
  productLine: productLineFragment,
}: Props) {
  const productLine = getFragmentData(fragment, productLineFragment);

  const defaultValues = useMemo<ProductLineInputs | undefined>(
    () =>
      productLine
        ? {
            ...pick(productLine, productLineFields),
            productCategoryIds:
              productLine.productLineCategoryDepartmentSubDepartmentsList?.map(
                (c) => {
                  assertPresence(c.productCategoryDepartmentSubDepartment?.id);
                  return c.productCategoryDepartmentSubDepartment.id;
                },
              ),
          }
        : undefined,
    [productLine],
  );

  const [updateProductLine, { loading: isSaving }] = useBrandMutation(mutation);

  const [handleValidSubmit] = useCreateValidSubmitHandler(
    useCallback<PerformUpdateHandler<ProductLineInputs, UpdatedProductLine>>(
      async (formData, { formContext }) => {
        const hasUnitPriceUpdate =
          !!formContext.formState.dirtyFields.wholesaleUnitPrice;

        const { productCategoryIds = [], ...productLineData } = formData;

        assertPresence(productLine?.id);

        const { data: updateData } = await updateProductLine({
          variables: {
            productLineId: productLine.id,
            productLineData,
            productCategoryIds,
            hasUnitPriceUpdate,
          },
        });
        const pl = updateData?.updateProductLine?.productLine;
        assertPresence(pl);
        return pl;
      },
      [updateProductLine, productLine?.id],
    ),
    {
      getSuccessToast: (result) => ({
        title: `Product line updated successfully: ${result?.name}`,
      }),
      processApolloError,
      onUpdateComplete: onComplete,
    },
  );

  return (
    <>
      <DrawerBody>
        <ProductLinePanelForm
          isLoading={isLoading}
          defaultValues={defaultValues}
          onValidSubmit={handleValidSubmit}
        />
      </DrawerBody>

      <DrawerFooter gap={4}>
        {onCancel && <CancelButton onClick={onCancel} />}

        <SaveButton isLoading={isSaving} form={FORM_ID} />
      </DrawerFooter>
    </>
  );
}
